import { invalidateEditorUndoToast } from "./editorUndoToastLifecycle";
import { getSpaceAddPersistBarrierSnapshot } from "./spaceAddPersistBarrier";
import { canonicalEditorImportProject, isEditorImportExecutionCurrent, useUploadStore } from "../../store/uploadStore";
import { useViewRDataStore } from "../../store/viewRDataStore";
import { getCanonicalSpaceSnapshotForUpload, getUploadSpaceTargetForImport } from "./spaceNavigationCanonicalState";
import { isUnityRuntimeReadyAtLeast } from "./unityRuntimeBridgeState";

export function assertEditorImportExecution(id: string, executionId: string): void {
  if (!isEditorImportExecutionCurrent(id, executionId)) throw new DOMException("Import execution ended", "AbortError");
}

export function isEditorImportOriginalContext(id: string): boolean {
  const item = useUploadStore.getState().items[id];
  const data = useViewRDataStore.getState().viewRData;
  const canonicalIndex = getCanonicalSpaceSnapshotForUpload().currentSpaceIndex;
  const fallback = getUploadSpaceTargetForImport();
  const currentIndex = canonicalIndex ?? (fallback.readiness === "ready" ? fallback.currentSpaceIndex : null);
  return Boolean(item && window.location.pathname.startsWith("/editor") && isUnityRuntimeReadyAtLeast("handlers_ready") &&
    canonicalEditorImportProject(data?.viewrGCSPath) === canonicalEditorImportProject(item.originViewrGcsPath) &&
    canonicalEditorImportProject(item.originViewrGcsPath) &&
    currentIndex === item.targetSpaceIndex);
}

export function assertEditorImportOriginalContext(id: string, executionId: string): void {
  assertEditorImportExecution(id, executionId);
  if (!isEditorImportOriginalContext(id)) throw new Error("editor_import_original_context_unavailable");
  const item = useUploadStore.getState().items[id];
  const space = useViewRDataStore.getState().viewRData?.spaces?.[item.targetSpaceIndex!];
  if (!space || (item.targetSpaceName && space.name !== item.targetSpaceName)) throw new Error("editor_import_target_space_changed");
}

/** File/closure remains in the app runtime. Returning to the original context
 * continues this exact stage; a browser reload cannot reconstruct the File. */
export async function waitForEditorImportOriginalContext(id: string, executionId: string): Promise<void> {
  while (!isEditorImportOriginalContext(id)) {
    assertEditorImportExecution(id, executionId);
    const item = useUploadStore.getState().items[id];
    const barrier = getSpaceAddPersistBarrierSnapshot();
    if (barrier.status === "failed" && barrier.targetSpaceIndex === item.targetSpaceIndex) throw new Error("editor_import_target_space_persist_failed");
    if (item.editorImportRuntimeState !== "awaiting_user_restore")
      useUploadStore.getState().upsertItem({ id, editorImportRuntimeState: "awaiting_user_restore" });
    await new Promise((resolve) => window.setTimeout(resolve, 100));
  }
  assertEditorImportOriginalContext(id, executionId);
  if (useUploadStore.getState().items[id].editorImportRuntimeState === "awaiting_user_restore")
    useUploadStore.getState().upsertItem({ id, editorImportRuntimeState: null });
}

/** Called by the actual send guard, never by a result-time Job lookup. */
export function markEditorImportRuntimeDispatched(id: string, executionId: string): void {
  assertEditorImportOriginalContext(id, executionId);
  const item = useUploadStore.getState().items[id];
  if (item.editorImportAdmission?.runtimeExecutionId !== executionId) {
    invalidateEditorUndoToast();
    useUploadStore.getState().updateEditorImportAdmission(id, executionId, { runtimeStarted: true, runtimeExecutionId: executionId });
  }
}
